import { Component, computed, inject, LOCALE_ID, signal } from '@angular/core';
import { InternalToolbar } from '../../components/internal-toolbar/internal-toolbar';
import { ReactiveIconTextButton } from '../../components/reactive-icon-text-button/reactive-icon-text-button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../api.service';
import { TAGPresence } from '../../../models/tagpresence';
import { TAGUser } from '../../../models/taguser';
import { DatePipe, formatDate } from '@angular/common';
import { FloatLabelModule } from 'primeng/floatlabel';
import { DatePickerModule } from 'primeng/datepicker';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MatIcon } from '../../components/mat-icon/mat-icon';
import {
  ActionConfirmationDialog,
  ActionConfirmationInfo,
} from '../../components/action-confirmation/action-confirmation-dialog';
import { DialogService } from 'primeng/dynamicdialog';
import {
  newProgressDialogStep,
  openProgressDialog,
  ProgressDialogStep,
  ProgressDialogStepState,
} from '../../components/progressDialog/progressDialog';

@Component({
  selector: 'app-internal-presence-edit',
  imports: [
    InternalToolbar,
    ReactiveIconTextButton,
    TranslatePipe,
    DatePipe,
    FloatLabelModule,
    DatePickerModule,
    ReactiveFormsModule,
    FormsModule,
    FieldsetModule,
    ToggleSwitchModule,
    MatIcon,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './internal-presence-edit.html',
  styleUrl: './internal-presence-edit.scss',
})
export class InternalPresenceEdit {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private locale = inject(LOCALE_ID);
  public api = inject(ApiService);

  presenceForm = new FormGroup({
    startdate: new FormControl(new Date(), [Validators.required]),
  });

  presence = signal<TAGPresence>(undefined);
  loadingPresence = signal(true);

  users = signal<TAGUser[]>([]);
  loadingUsers = signal(true);
  seenUsers = signal<string[]>([]);
  filteredUsers = computed(() => {
    console.log(this.seenUsers(), this.users());
    return this.seenUsers().filter((u) => {
      return this.showHiddenUsers() || !this.getUserHidden(u);
    });
  });

  showHiddenUsers = signal(false);

  getPresenceColor(value: number) {
    return TAGPresence.getColor(value);
  }

  getPresenceIcon(value: number) {
    return TAGPresence.getIcon(value);
  }

  getUserColor(user: string) {
    return this.presence().getUserColor(user);
  }

  getUserIcon(user: string) {
    return this.presence().getUserIcon(user);
  }

  getUserHidden(user: string) {
    return this.users().find((u) => u.username == user)?.hidden ?? false;
  }

  getUserName(username: string) {
    var usr = this.users().find((u) => u.username == username);
    if (usr != undefined) return usr.firstname + ' ' + usr.lastname;

    return username;
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      this.api.waitForAPI().then(() => {
        this.loadPresence(params['presenceid']);

        this.api
          .getUsers()
          .then((users: TAGUser[]) => {
            this.users.set(users);

            let newUsernames = [];
            for (let user of this.users()) {
              if (!this.seenUsers().includes(user.username)) newUsernames.push(user.username);
            }

            this.seenUsers.update((u) => [...u, ...newUsernames]);

            this.loadingUsers.set(false);
          })
          .catch((reason) => {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.translate('errors.couldNotLoadUsers')(),
              detail: reason.message,
            });
          });
      });
    });
  }

  loadPresence(presenceid: string) {
    if (presenceid === 'new') {
      let newPresence = new TAGPresence();

      let newStartDate = new Date();
      let fridayDiff = newStartDate.getDay() - 5;
      if (fridayDiff < 0) fridayDiff += 7;
      newStartDate = new Date(newStartDate.getTime() - 1000 * 60 * 60 * 24 * fridayDiff);
      newStartDate.setHours(14, 0, 0);
      newPresence.startdate = newStartDate;

      let newEndDate = new Date(newStartDate.getTime() + 1000 * 60 * 90);
      newPresence.enddate = newEndDate;

      this.presence.set(newPresence);
      this.presenceForm.patchValue(newPresence);

      this.loadingPresence.set(false);
    } else {
      this.api
        .getPresence(presenceid)
        .then((presence: TAGPresence) => {
          this.presence.set(presence);

          let newUsernames = [];
          for (let username of Object.keys(this.presence().presenceInfo)) {
            if (!this.seenUsers().includes(username)) newUsernames.push(username);
          }

          this.seenUsers.update((u) => [...u, ...newUsernames]);

          this.presenceForm.patchValue(presence);

          this.loadingPresence.set(false);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate('errors.couldNotLoadPresenceInformation')(),
            detail: reason.message,
          });
        });
    }
  }

  changePresence(user: string) {
    var oldVal = this.presence().presenceInfo[user] ?? -1;
    oldVal++;
    if (oldVal > 5) oldVal = -1;

    if (oldVal == 1) oldVal = 3; // Skip due to no covid
    if (oldVal == 2) oldVal = 3; // Skip due to no covid

    this.presence().presenceInfo[user] = oldVal;
    this.presence.update((p) => {
      p.presenceInfo[user] = oldVal;
      return new TAGPresence(p.serialize());
    });
  }

  deletePresence() {
    if (this.presence()._id == undefined) return;

    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.presence.deleteTitle', {
          presence: this.presence(),
        })(),
        this.translate.translate('internal.presence.deleteMessage', {
          presence: this.presence(),
          date: formatDate(this.presence().startdate, 'shortDate', this.locale),
        })(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.presence.deleteProgress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.deletePresence(this.presence()._id);
            deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.cancelPresence();
          } catch (reason) {
            deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            deleteStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }

  cancelPresence() {
    this.router.navigate(['..'], { relativeTo: this.route });
  }

  savePresence() {
    if (!this.presenceForm.valid) return;

    let updateStep = newProgressDialogStep(
      new ProgressDialogStep(
        ProgressDialogStepState.WAITING,
        this.translate.translate('internal.presence.updateProgress')(),
      ),
    );

    if (this.presence()._id == undefined) {
      updateStep = newProgressDialogStep(
        new ProgressDialogStep(
          ProgressDialogStepState.WAITING,
          this.translate.translate('internal.presence.createProgress')(),
        ),
      );
    }

    let progressDialog = openProgressDialog(this.dialogService, [updateStep]);

    progressDialog.onChildComponentLoaded.subscribe((pDialog) => {
      updateStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

      let presence = this.presence();
      presence.startdate = this.presenceForm.value.startdate;

      Object.keys(presence.presenceInfo).forEach((key, val) => {
        if (val == -1) delete presence.presenceInfo[key];
      });

      if (this.presence()._id == undefined) {
        this.api
          .newPresence(presence)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.cancelPresence();
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      } else {
        this.api
          .updatePresence(presence)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.cancelPresence();
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      }
    });
  }
}
